import Cookies from 'js-cookie';
import {
  type RolloutBatch,
  ROLLOUT_BATCHES,
  BATCH_RELEASE_DATE_MAP,
  IDENTITY_MANDATORY_MODAL_DATE,
} from '../../constants';
import { getIdentityTestingFlag } from '../../utils';

const IDENTITY_MODAL_DISMISSED_STORAGE_KEY = 'DRAFT_IDENTITY_MODAL_DISMISSED';

interface BatchDeterminationParams {
  selectedOrgFprint: string;
  clioTokenPresenceByOrg: Record<string, boolean>;
  orgCreatedAt: string;
}

interface ModalDisplayParams {
  identityInviteFprint: string;
  isIdentityAuthenticated: boolean;
  isIdentityLoginDisabled: boolean;
  selectedOrgFprint: string;
  clioTokenPresenceByOrg: Record<string, boolean>;
  orgCreatedAt: string;
  orgIsInternal: boolean;
  userEmail: string;
}

export const setIdentityModalDismissed = (): void => {
  const dayFromNow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  Cookies.set(IDENTITY_MODAL_DISMISSED_STORAGE_KEY, 'true', {
    expires: dayFromNow,
  });
};

export const getIdentityModalDismissed = (): boolean => {
  return Cookies.get(IDENTITY_MODAL_DISMISSED_STORAGE_KEY) === 'true';
};

export const determineRolloutBatch = ({
  selectedOrgFprint,
  clioTokenPresenceByOrg,
  orgCreatedAt,
}: BatchDeterminationParams): RolloutBatch => {
  if (!selectedOrgFprint || !clioTokenPresenceByOrg) {
    return ROLLOUT_BATCHES.FINAL;
  }

  const orgCreationDate = new Date(orgCreatedAt);

  if (!clioTokenPresenceByOrg[selectedOrgFprint]) {
    if (orgCreationDate >= new Date('2025-01-01')) {
      return ROLLOUT_BATCHES.Y_2025_ALL;
    }
    if (orgCreationDate >= new Date('2024-01-01')) {
      return ROLLOUT_BATCHES.Y_2024_NO_SYNC;
    }
    return ROLLOUT_BATCHES.BEFORE_2024_NO_SYNC;
  }

  const lowerBoundOrgDateToBatchMap = [
    { date: new Date('2025-01-01'), batch: ROLLOUT_BATCHES.Y_2025_ALL },
    { date: new Date('2024-07-01'), batch: ROLLOUT_BATCHES.H2_2024_ALL },
    { date: new Date('2024-01-01'), batch: ROLLOUT_BATCHES.H1_2024_ALL },
    { date: new Date('2023-01-01'), batch: ROLLOUT_BATCHES.Y_2023_ALL },
    { date: new Date('2022-01-01'), batch: ROLLOUT_BATCHES.Y_2022_ALL },
  ];

  for (const cutoff of lowerBoundOrgDateToBatchMap) {
    if (orgCreationDate >= cutoff.date) {
      return cutoff.batch;
    }
  }

  return ROLLOUT_BATCHES.BEFORE_2022_ALL;
};

export const shouldShowIdentityModal = ({
  identityInviteFprint,
  isIdentityAuthenticated,
  isIdentityLoginDisabled,
  selectedOrgFprint,
  clioTokenPresenceByOrg,
  orgCreatedAt,
  orgIsInternal,
  userEmail,
}: ModalDisplayParams): boolean => {
  if (isIdentityLoginDisabled) {
    return false;
  }
  if (orgIsInternal) {
    return false;
  }
  if (isIdentityAuthenticated || getIdentityModalDismissed()) {
    return false;
  }
  if (identityInviteFprint || getIdentityTestingFlag()) {
    return true;
  }
  if (userEmail.endsWith('@clio.com')) {
    return false;
  }

  const today = new Date();
  const rolloutBatch = determineRolloutBatch({
    selectedOrgFprint,
    clioTokenPresenceByOrg,
    orgCreatedAt,
  });
  const batchReleaseDate = BATCH_RELEASE_DATE_MAP[rolloutBatch];

  const mandatoryDate = new Date(IDENTITY_MANDATORY_MODAL_DATE);

  if (today > mandatoryDate) {
    return true;
  }

  return today >= batchReleaseDate;
};

export const isIdentityModalDismissible = (): boolean => {
  const today = new Date();
  return today < new Date(IDENTITY_MANDATORY_MODAL_DATE);
};
